import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { 
  Eye, 
  MousePointerClick, 
  ShoppingCart, 
  DollarSign,
  Package,
  RefreshCw,
  ArrowLeft,
  TrendingUp,
  Clock,
  User,
  Mail,
  MessageCircle,
  Bell,
  Star,
  Copy,
  Ticket,
  Download,
  BarChart3,
  Calendar,
  Trash2,
  Image,
  Phone,
  MapPin,
  Truck
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  LineChart,
  Line
} from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [newStock, setNewStock] = useState("");
  const [updatingStock, setUpdatingStock] = useState(false);
  const [testingNotification, setTestingNotification] = useState(false);
  
  // Review codes state
  const [reviewCodes, setReviewCodes] = useState([]);
  const [generatingCodes, setGeneratingCodes] = useState(false);
  const [codeCount, setCodeCount] = useState(1);
  const [customerNames, setCustomerNames] = useState("");
  
  // Reviews management state
  const [allReviews, setAllReviews] = useState([]);
  const [deletingReview, setDeletingReview] = useState(null);
  
  // Purchases state
  const [allPurchases, setAllPurchases] = useState([]);
  const [loadingPurchases, setLoadingPurchases] = useState(false);
  
  // Charts state
  const [weeklyData, setWeeklyData] = useState([]);
  const [monthlyData, setMonthlyData] = useState([]);
  const [activeChart, setActiveChart] = useState("weekly"); // "weekly" or "monthly"
  const [loadingCharts, setLoadingCharts] = useState(false);
  
  // Visitor locations state
  const [visitorLocations, setVisitorLocations] = useState([]);
  const [totalTracked, setTotalTracked] = useState(0);

  useEffect(() => {
    checkAdminAccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading]);

  const checkAdminAccess = async () => {
    if (authLoading) return;
    
    try {
      const response = await axios.get(`${API}/admin/check`, { withCredentials: true });
      if (response.data.is_admin) {
        setIsAdmin(true);
        fetchAnalytics();
        fetchReviewCodes();
        fetchChartData();
        fetchAllReviews();
        fetchAllPurchases();
        fetchVisitorLocations();
      } else {
        toast.error("Acceso denegado");
        navigate("/");
      }
    } catch (error) {
      toast.error("Error al verificar acceso");
      navigate("/");
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/admin/analytics`, { withCredentials: true });
      setAnalytics(response.data);
      setNewStock(response.data.current_stock.toString());
    } catch (error) {
      toast.error("Error al cargar analytics");
    }
  };

  const fetchChartData = async () => {
    setLoadingCharts(true);
    try {
      const [weeklyRes, monthlyRes] = await Promise.all([
        axios.get(`${API}/admin/analytics/weekly`, { withCredentials: true }),
        axios.get(`${API}/admin/analytics/monthly`, { withCredentials: true })
      ]);
      setWeeklyData(weeklyRes.data.data || []);
      setMonthlyData(monthlyRes.data.data || []);
    } catch (error) {
      console.error("Chart data fetch failed:", error);
    } finally {
      setLoadingCharts(false);
    }
  };

  const downloadHistory = () => {
    window.open(`${API}/admin/analytics/export`, '_blank');
  };

  const fetchReviewCodes = async () => {
    try {
      const response = await axios.get(`${API}/admin/review-codes`, { withCredentials: true });
      setReviewCodes(response.data.codes || []);
    } catch (error) {
      console.error("Review codes fetch failed:", error);
    }
  };

  const generateReviewCodes = async () => {
    setGeneratingCodes(true);
    try {
      const names = customerNames.split(',').map(n => n.trim()).filter(n => n);
      const response = await axios.post(`${API}/admin/review-codes/generate`, {
        count: codeCount,
        customer_names: names
      }, { withCredentials: true });
      
      toast.success(`Se generaron ${response.data.codes.length} códigos`);
      setCustomerNames("");
      fetchReviewCodes();
    } catch (error) {
      toast.error("Error al generar códigos");
    } finally {
      setGeneratingCodes(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Código copiado");
  };

  const fetchAllReviews = async () => {
    try {
      const response = await axios.get(`${API}/admin/reviews`, { withCredentials: true });
      setAllReviews(response.data.reviews || []);
    } catch (error) {
      console.error("Reviews fetch failed:", error);
    }
  };

  const fetchAllPurchases = async () => {
    setLoadingPurchases(true);
    try {
      const response = await axios.get(`${API}/admin/purchases`, { withCredentials: true });
      setAllPurchases(response.data.purchases || []);
    } catch (error) {
      console.error("Purchases fetch failed:", error);
    } finally {
      setLoadingPurchases(false);
    }
  };

  const fetchVisitorLocations = async () => {
    try {
      const response = await axios.get(`${API}/admin/visitor-locations`, { withCredentials: true });
      setVisitorLocations(response.data.locations || []);
      setTotalTracked(response.data.total_tracked || 0);
    } catch (error) {
      console.error("Visitor locations fetch failed:", error);
    }
  };

  const deleteReview = async (reviewId) => {
    if (!confirm("¿Eliminar esta reseña? El código podrá usarse de nuevo.")) return;
    
    setDeletingReview(reviewId);
    try {
      await axios.delete(`${API}/admin/reviews/${reviewId}`, { withCredentials: true });
      toast.success("Reseña eliminada. El código puede usarse de nuevo.");
      fetchAllReviews();
      fetchReviewCodes();
    } catch (error) {
      toast.error("Error al eliminar reseña");
    } finally {
      setDeletingReview(null);
    }
  };

  const handleUpdateStock = async () => {
    const stockValue = parseInt(newStock);
    if (isNaN(stockValue) || stockValue < 0) {
      toast.error("Ingresa un número válido");
      return;
    }

    setUpdatingStock(true);
    try {
      await axios.put(`${API}/admin/stock`, { stock: stockValue }, { withCredentials: true });
      toast.success("Stock actualizado correctamente");
      fetchAnalytics();
    } catch (error) {
      toast.error("Error al actualizar stock");
    } finally {
      setUpdatingStock(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleString("es-MX", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const getEventIcon = (type) => {
    switch (type) {
      case "visit": return <Eye className="w-4 h-4 text-blue-500" />;
      case "buy_click": return <MousePointerClick className="w-4 h-4 text-orange-500" />;
      case "purchase": return <ShoppingCart className="w-4 h-4 text-green-500" />;
      default: return <Clock className="w-4 h-4 text-gray-500" />;
    }
  };

  const getEventLabel = (type) => {
    switch (type) {
      case "visit": return "Visita";
      case "buy_click": return "Clic en Comprar";
      case "purchase": return "Compra completada";
      default: return type;
    }
  };

  if (loading || authLoading) {
    return (
      <div className="min-h-screen bg-bone flex items-center justify-center">
        <div className="animate-spin w-12 h-12 border-4 border-agave border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-bone">
      {/* Header */}
      <header className="bg-agave text-white py-4 px-4 md:px-8 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => navigate("/")}
              className="text-white hover:bg-white/20"
              data-testid="admin-back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Volver
            </Button>
            <h1 className="font-gluten text-xl md:text-2xl">Panel de Administración</h1>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={fetchAnalytics}
            className="text-white hover:bg-white/20"
            data-testid="admin-refresh-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualizar
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card className="bg-white" data-testid="stat-visits">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-fredoka text-muted-foreground flex items-center gap-2">
                  <Eye className="w-4 h-4 text-blue-500" />
                  Visitas
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl md:text-3xl font-bold text-foreground">
                  {analytics?.total_visits || 0}
                </p>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card className="bg-white" data-testid="stat-clicks">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-fredoka text-muted-foreground flex items-center gap-2">
                  <MousePointerClick className="w-4 h-4 text-orange-500" />
                  Clics Compra
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl md:text-3xl font-bold text-foreground">
                  {analytics?.total_buy_clicks || 0}
                </p>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Card className="bg-white" data-testid="stat-purchases">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-fredoka text-muted-foreground flex items-center gap-2">
                  <ShoppingCart className="w-4 h-4 text-green-500" />
                  Compras
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl md:text-3xl font-bold text-foreground">
                  {analytics?.total_purchases || 0}
                </p>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <Card className="bg-white" data-testid="stat-revenue">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-fredoka text-muted-foreground flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-emerald-500" />
                  Ingresos
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl md:text-3xl font-bold text-foreground">
                  ${analytics?.total_revenue?.toLocaleString() || 0}
                </p>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <Card className={`${analytics?.current_stock === 0 ? 'bg-red-50 border-red-200' : 'bg-white'}`} data-testid="stat-stock">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-fredoka text-muted-foreground flex items-center gap-2">
                  <Package className={`w-4 h-4 ${analytics?.current_stock === 0 ? 'text-red-500' : 'text-purple-500'}`} />
                  Stock
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className={`text-2xl md:text-3xl font-bold ${analytics?.current_stock === 0 ? 'text-red-600' : 'text-foreground'}`}>
                  {analytics?.current_stock || 0}
                </p>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Charts Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
          className="mt-8"
        >
          <Card className="bg-white">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-fredoka flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-agave" />
                Análisis de Actividad
              </CardTitle>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant={activeChart === "weekly" ? "default" : "outline"}
                  onClick={() => setActiveChart("weekly")}
                  className={activeChart === "weekly" ? "bg-agave hover:bg-agave/90" : ""}
                >
                  <Calendar className="w-4 h-4 mr-1" />
                  Semanal
                </Button>
                <Button
                  size="sm"
                  variant={activeChart === "monthly" ? "default" : "outline"}
                  onClick={() => setActiveChart("monthly")}
                  className={activeChart === "monthly" ? "bg-agave hover:bg-agave/90" : ""}
                >
                  <Calendar className="w-4 h-4 mr-1" />
                  Mensual
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={downloadHistory}
                  className="border-mexican text-mexican hover:bg-mexican/10"
                >
                  <Download className="w-4 h-4 mr-1" />
                  Descargar CSV
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loadingCharts ? (
                <div className="flex items-center justify-center h-64">
                  <RefreshCw className="w-8 h-8 animate-spin text-agave" />
                </div>
              ) : (
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    {activeChart === "weekly" ? (
                      <BarChart data={weeklyData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                        <XAxis 
                          dataKey="day_name" 
                          tick={{ fill: '#666', fontSize: 12 }}
                        />
                        <YAxis tick={{ fill: '#666', fontSize: 12 }} />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: '#fff', 
                            border: '1px solid #e0e0e0',
                            borderRadius: '8px',
                            fontFamily: 'Nunito'
                          }}
                          labelFormatter={(value) => `Día: ${value}`}
                        />
                        <Legend />
                        <Bar 
                          dataKey="visits" 
                          name="Visitas" 
                          fill="#6b7280" 
                          radius={[4, 4, 0, 0]}
                        />
                        <Bar 
                          dataKey="purchases" 
                          name="Compras" 
                          fill="#e85d75" 
                          radius={[4, 4, 0, 0]}
                        />
                      </BarChart>
                    ) : (
                      <LineChart data={monthlyData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                        <XAxis 
                          dataKey="day" 
                          tick={{ fill: '#666', fontSize: 12 }}
                          label={{ value: 'Día del mes', position: 'bottom', fill: '#666' }}
                        />
                        <YAxis tick={{ fill: '#666', fontSize: 12 }} />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: '#fff', 
                            border: '1px solid #e0e0e0',
                            borderRadius: '8px',
                            fontFamily: 'Nunito'
                          }}
                          labelFormatter={(value) => `Día ${value}`}
                        />
                        <Legend />
                        <Line 
                          type="monotone" 
                          dataKey="visits" 
                          name="Visitas" 
                          stroke="#6b7280" 
                          strokeWidth={2}
                          dot={{ fill: '#6b7280', strokeWidth: 2, r: 4 }}
                        />
                        <Line 
                          type="monotone" 
                          dataKey="purchases" 
                          name="Compras" 
                          stroke="#e85d75" 
                          strokeWidth={2}
                          dot={{ fill: '#e85d75', strokeWidth: 2, r: 4 }}
                        />
                      </LineChart>
                    )}
                  </ResponsiveContainer>
                </div>
              )}
              
              {/* Summary Stats for selected period */}
              <div className="grid grid-cols-3 gap-4 mt-6 pt-4 border-t">
                <div className="text-center">
                  <p className="font-nunito text-sm text-muted-foreground">Total Visitas</p>
                  <p className="font-fredoka text-2xl text-agave">
                    {(activeChart === "weekly" ? weeklyData : monthlyData).reduce((sum, d) => sum + d.visits, 0)}
                  </p>
                </div>
                <div className="text-center">
                  <p className="font-nunito text-sm text-muted-foreground">Total Compras</p>
                  <p className="font-fredoka text-2xl text-mexican">
                    {(activeChart === "weekly" ? weeklyData : monthlyData).reduce((sum, d) => sum + d.purchases, 0)}
                  </p>
                </div>
                <div className="text-center">
                  <p className="font-nunito text-sm text-muted-foreground">Ingresos</p>
                  <p className="font-fredoka text-2xl text-emerald-600">
                    ${(activeChart === "weekly" ? weeklyData : monthlyData).reduce((sum, d) => sum + d.revenue, 0).toLocaleString()}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8 mt-8">
          {/* Stock Management */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Card className="bg-white" data-testid="stock-management">
              <CardHeader>
                <CardTitle className="font-fredoka flex items-center gap-2">
                  <Package className="w-5 h-5 text-agave" />
                  Control de Inventario
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="font-nunito text-sm text-muted-foreground mb-2 block">
                    Stock Actual
                  </label>
                  <div className="flex gap-2">
                    <Input
                      type="number"
                      min="0"
                      value={newStock}
                      onChange={(e) => setNewStock(e.target.value)}
                      className="font-nunito"
                      data-testid="stock-input"
                    />
                    <Button
                      onClick={handleUpdateStock}
                      disabled={updatingStock}
                      className="bg-agave hover:bg-agave/90"
                      data-testid="stock-update-btn"
                    >
                      {updatingStock ? "..." : "Actualizar"}
                    </Button>
                  </div>
                </div>
                
                {analytics?.current_stock === 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="font-fredoka text-red-600 text-sm">
                      ⚠️ Stock agotado - Los clientes verán "Agotado" en la página
                    </p>
                  </div>
                )}

                <div className="bg-bone rounded-lg p-4">
                  <h4 className="font-fredoka text-sm mb-2">Resumen</h4>
                  <div className="space-y-1 font-nunito text-sm text-muted-foreground">
                    <p>Packs vendidos: {analytics?.total_purchases || 0}</p>
                    <p>Ingresos totales: ${analytics?.total_revenue?.toLocaleString() || 0} MXN</p>
                    <p>Precio por pack: $500 MXN + envío opcional $150</p>
                  </div>
                </div>

                {/* WhatsApp Notification Test */}
                <div className="border-t pt-4">
                  <h4 className="font-fredoka text-sm mb-3 flex items-center gap-2">
                    <Bell className="w-4 h-4 text-green-500" />
                    Notificaciones WhatsApp
                  </h4>
                  <Button
                    onClick={async () => {
                      setTestingNotification(true);
                      try {
                        const response = await axios.post(`${API}/admin/test-notification`, {}, { withCredentials: true });
                        if (response.data.status === "success") {
                          toast.success("Notificación enviada", {
                            description: "Revisa tu WhatsApp"
                          });
                        } else {
                          toast.error("Error al enviar notificación");
                        }
                      } catch (error) {
                        toast.error("Error al enviar notificación");
                      } finally {
                        setTestingNotification(false);
                      }
                    }}
                    disabled={testingNotification}
                    variant="outline"
                    className="w-full border-green-500 text-green-600 hover:bg-green-50"
                    data-testid="test-notification-btn"
                  >
                    <MessageCircle className="w-4 h-4 mr-2" />
                    {testingNotification ? "Enviando..." : "Probar Notificación"}
                  </Button>
                  <p className="font-nunito text-xs text-muted-foreground mt-2">
                    Recibirás una notificación de prueba en tu WhatsApp
                  </p>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Recent Activity */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
            className="lg:col-span-2"
          >
            <Card className="bg-white" data-testid="recent-activity">
              <CardHeader>
                <CardTitle className="font-fredoka flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-agave" />
                  Actividad Reciente
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="max-h-[400px] overflow-y-auto space-y-2">
                  {analytics?.recent_activity?.length > 0 ? (
                    analytics.recent_activity.map((event, index) => (
                      <div 
                        key={`activity-${event.type}-${event.timestamp}-${index}`}
                        className="flex items-center gap-3 p-3 bg-bone rounded-lg"
                        data-testid={`activity-${index}`}
                      >
                        {getEventIcon(event.type)}
                        <div className="flex-1 min-w-0">
                          <p className="font-fredoka text-sm text-foreground">
                            {getEventLabel(event.type)}
                          </p>
                          {event.details?.email && (
                            <p className="font-nunito text-xs text-muted-foreground flex items-center gap-1">
                              <Mail className="w-3 h-3" />
                              {event.details.email}
                            </p>
                          )}
                          {event.details?.amount && (
                            <p className="font-nunito text-xs text-green-600">
                              ${event.details.amount} MXN
                            </p>
                          )}
                          {event.location?.city && (
                            <p className="font-nunito text-xs text-muted-foreground flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {event.location.city}, {event.location.region}
                            </p>
                          )}
                        </div>
                        <span className="font-nunito text-xs text-muted-foreground whitespace-nowrap">
                          {formatDate(event.timestamp)}
                        </span>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8">
                      <Clock className="w-12 h-12 text-muted-foreground mx-auto mb-3 opacity-50" />
                      <p className="font-nunito text-muted-foreground">
                        No hay actividad registrada aún
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Visitor Locations Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mt-8"
        >
          <Card className="bg-white" data-testid="visitor-locations">
            <CardHeader>
              <CardTitle className="font-fredoka flex items-center gap-2">
                <MapPin className="w-5 h-5 text-agave" />
                Ubicación de Visitantes
                {totalTracked > 0 && (
                  <span className="font-nunito text-sm font-normal text-muted-foreground ml-2">
                    ({totalTracked} visitas rastreadas)
                  </span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {visitorLocations.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {visitorLocations.map((loc, locIdx) => (
                    <div 
                      key={`loc-${loc.city}-${loc.region}`}
                      className="flex items-center justify-between p-3 bg-bone rounded-lg"
                      data-testid={`location-${locIdx}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-agave/10 rounded-full flex items-center justify-center flex-shrink-0">
                          <MapPin className="w-4 h-4 text-agave" />
                        </div>
                        <div>
                          <p className="font-fredoka text-sm">{loc.city}</p>
                          <p className="font-nunito text-xs text-muted-foreground">
                            {loc.region}, {loc.country}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-fredoka text-lg text-agave">{loc.count}</p>
                        <p className="font-nunito text-xs text-muted-foreground">visitas</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <MapPin className="w-12 h-12 text-muted-foreground mx-auto mb-3 opacity-50" />
                  <p className="font-nunito text-muted-foreground">
                    Las ubicaciones se registrarán con las próximas visitas
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Detailed Purchases Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.45 }}
          className="mt-8"
        >
          <Card className="bg-white" data-testid="purchases-list">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-fredoka flex items-center gap-2">
                <ShoppingCart className="w-5 h-5 text-mexican" />
                Historial de Compras ({allPurchases.length})
              </CardTitle>
              <Button
                size="sm"
                variant="outline"
                onClick={fetchAllPurchases}
                disabled={loadingPurchases}
              >
                <RefreshCw className={`w-4 h-4 mr-1 ${loadingPurchases ? 'animate-spin' : ''}`} />
                Actualizar
              </Button>
            </CardHeader>
            <CardContent>
              {loadingPurchases ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="w-8 h-8 animate-spin text-agave" />
                </div>
              ) : allPurchases.length > 0 ? (
                <div className="space-y-4 max-h-[500px] overflow-y-auto">
                  {allPurchases.map((purchase, index) => (
                    <div 
                      key={purchase.id || index}
                      className="p-4 bg-bone rounded-xl border border-gray-100"
                    >
                      <div className="flex flex-wrap items-start justify-between gap-4">
                        <div className="flex-1 min-w-[200px]">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="font-fredoka font-bold text-agave">
                              #{purchase.id?.slice(-8) || index + 1}
                            </span>
                            <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full font-nunito">
                              Pagado
                            </span>
                          </div>
                          
                          <div className="space-y-1">
                            <div className="flex items-center gap-2 font-nunito text-sm">
                              <Package className="w-4 h-4 text-muted-foreground" />
                              <span>{purchase.quantity || 1}x Pack GATOFELIZ</span>
                            </div>
                            
                            <div className="flex items-center gap-2 font-nunito text-sm">
                              <DollarSign className="w-4 h-4 text-muted-foreground" />
                              <span className="font-bold text-mexican">${purchase.amount?.toLocaleString() || 0} MXN</span>
                              {purchase.shipping_cost > 0 && (
                                <span className="text-xs text-muted-foreground">(incluye envío: ${purchase.shipping_cost})</span>
                              )}
                            </div>
                            
                            {purchase.customer_phone && (
                              <div className="flex items-center gap-2 font-nunito text-sm">
                                <Phone className="w-4 h-4 text-muted-foreground" />
                                <span>{purchase.customer_phone}</span>
                              </div>
                            )}
                            
                            {purchase.customer_email && (
                              <div className="flex items-center gap-2 font-nunito text-sm">
                                <Mail className="w-4 h-4 text-muted-foreground" />
                                <span>{purchase.customer_email}</span>
                              </div>
                            )}
                            
                            <div className="flex items-center gap-2 font-nunito text-sm">
                              {purchase.shipping_method === "delivery" ? (
                                <>
                                  <Truck className="w-4 h-4 text-muted-foreground" />
                                  <span>Envío a domicilio</span>
                                </>
                              ) : (
                                <>
                                  <MapPin className="w-4 h-4 text-muted-foreground" />
                                  <span>Recolección en Querétaro</span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        <div className="text-right">
                          <div className="flex items-center gap-1 font-nunito text-xs text-muted-foreground">
                            <Clock className="w-3 h-3" />
                            {purchase.created_at ? new Date(purchase.created_at).toLocaleDateString('es-MX', {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            }) : 'Fecha no disponible'}
                          </div>
                          {purchase.review_code && (
                            <div className="mt-1 font-mono text-xs text-agave">
                              Código: {purchase.review_code}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <ShoppingCart className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p className="font-nunito">No hay compras registradas</p>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Review Codes Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mt-8"
        >
          <Card className="bg-white" data-testid="review-codes">
            <CardHeader>
              <CardTitle className="font-fredoka flex items-center gap-2">
                <Star className="w-5 h-5 text-mexican" />
                Códigos de Reseña
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-6">
                {/* Generate Codes */}
                <div className="space-y-4">
                  <h4 className="font-fredoka text-sm flex items-center gap-2">
                    <Ticket className="w-4 h-4 text-agave" />
                    Generar Nuevos Códigos
                  </h4>
                  <div>
                    <label className="font-nunito text-sm text-muted-foreground mb-2 block">
                      Cantidad de códigos (máx. 10)
                    </label>
                    <Input
                      type="number"
                      min="1"
                      max="10"
                      value={codeCount}
                      onChange={(e) => setCodeCount(Math.min(10, Math.max(1, parseInt(e.target.value) || 1)))}
                      className="font-nunito"
                    />
                  </div>
                  <div>
                    <label className="font-nunito text-sm text-muted-foreground mb-2 block">
                      Nombres de clientes (separados por coma, opcional)
                    </label>
                    <Input
                      type="text"
                      placeholder="Ej: Juan Pérez, María García"
                      value={customerNames}
                      onChange={(e) => setCustomerNames(e.target.value)}
                      className="font-nunito"
                    />
                  </div>
                  <Button
                    onClick={generateReviewCodes}
                    disabled={generatingCodes}
                    className="w-full bg-mexican hover:bg-mexican/90"
                  >
                    <Star className="w-4 h-4 mr-2" />
                    {generatingCodes ? "Generando..." : `Generar ${codeCount} Código${codeCount > 1 ? 's' : ''}`}
                  </Button>
                </div>

                {/* Existing Codes */}
                <div className="space-y-4">
                  <h4 className="font-fredoka text-sm flex items-center gap-2">
                    <Copy className="w-4 h-4 text-agave" />
                    Códigos Existentes
                  </h4>
                  <div className="max-h-64 overflow-y-auto space-y-2">
                    {reviewCodes.length > 0 ? (
                      reviewCodes.map((code) => (
                        <div 
                          key={`code-${code.review_code}`}
                          className={`flex items-center justify-between p-3 rounded-lg ${code.review_used ? 'bg-gray-100' : 'bg-green-50'}`}
                        >
                          <div>
                            <p className={`font-mono font-bold ${code.review_used ? 'text-gray-400 line-through' : 'text-agave'}`}>
                              {code.review_code}
                            </p>
                            <p className="font-nunito text-xs text-muted-foreground">
                              {code.customer_email} {code.review_used && '(usado)'}
                            </p>
                          </div>
                          {!code.review_used && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => copyToClipboard(code.review_code)}
                              className="text-agave hover:text-agave/80"
                            >
                              <Copy className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-6 text-muted-foreground">
                        <Star className="w-8 h-8 mx-auto mb-2 opacity-30" />
                        <p className="font-nunito text-sm">No hay códigos generados</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Reviews Management Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="mt-8"
        >
          <Card className="bg-white" data-testid="reviews-management">
            <CardHeader>
              <CardTitle className="font-fredoka flex items-center gap-2">
                <Star className="w-5 h-5 text-mexican" />
                Gestión de Reseñas ({allReviews.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4 max-h-96 overflow-y-auto">
                {allReviews.length > 0 ? (
                  allReviews.map((review) => (
                    <div 
                      key={review.id}
                      className="flex items-start gap-4 p-4 bg-bone rounded-xl"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="font-fredoka font-bold text-agave">{review.customer_name}</p>
                          <div className="flex">
                            {[...Array(5)].map((_, i) => (
                              <Star 
                                key={i} 
                                className={`w-4 h-4 ${i < review.rating ? 'fill-mexican text-mexican' : 'text-gray-300'}`} 
                              />
                            ))}
                          </div>
                          {review.photos && review.photos.length > 0 && (
                            <span className="flex items-center gap-1 text-xs text-agave">
                              <Image className="w-3 h-3" />
                              {review.photos.length} foto(s)
                            </span>
                          )}
                        </div>
                        <p className="font-nunito text-sm text-gray-600 mb-2">"{review.comment}"</p>
                        <p className="font-nunito text-xs text-muted-foreground">
                          Código: {review.review_code} | {new Date(review.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => deleteReview(review.id)}
                        disabled={deletingReview === review.id}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        {deletingReview === review.id ? (
                          <RefreshCw className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Star className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p className="font-nunito">No hay reseñas aún</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </main>
    </div>
  );
}
